import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Table, Tag, Button, Alert, Tooltip, message } from 'antd';
import { ExclamationCircleTwoTone, LinkOutlined, MailOutlined } from '@ant-design/icons';
import OnDemandOrdersService from '../../../services/OnDemandOrdersService';
import PatientResultsService from '../../../services/PatientResultsService';

/**
 * Kit Results dialog for the Members tab. For the selected members, pulls the
 * latest lab results from Endless (by activation code) and shows status plus
 * an expandable biomarker table for anyone whose results are ready.
 */
const STATUS_META = {
  ready: { label: 'Results ready', color: 'green' },
  pending: { label: 'Awaiting results', color: 'gold' },
  not_activated: { label: 'Not activated', color: 'default' },
  not_found: { label: 'Not found', color: 'red' },
  error: { label: 'Error', color: 'red' },
  rejected: { label: 'Sample rejected', color: 'red' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

const BulkKitResultsModal = ({ visible, onClose, patientIds = [] }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const sync = useCallback(async () => {
    if (!patientIds.length) return;
    setLoading(true);
    try {
      const res = await OnDemandOrdersService.syncKitResults(patientIds);
      if (res.success) setRows(res.data || []);
      else message.error(res.message || 'Failed to load results');
    } catch (err) {
      message.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [patientIds]);

  useEffect(() => {
    if (visible) sync();
    else setRows([]);
  }, [visible, sync]);

  const readyCount = rows.filter((r) => r.status === 'ready').length;

  const copyLink = async (patientId) => {
    try {
      const res = await PatientResultsService.generateLink(patientId);
      if (res?.success && res.data?.url) {
        await navigator.clipboard.writeText(res.data.url);
        message.success('Patient results link copied to clipboard');
      } else {
        message.error(res?.message || 'Failed to create link');
      }
    } catch (err) {
      message.error('Failed to create link');
    }
  };

  const emailPatient = async (patientId) => {
    try {
      const res = await PatientResultsService.emailResults(patientId);
      if (res?.success) message.success('Results emailed to patient');
      else message.error(res?.message || 'Failed to email patient');
    } catch (err) {
      message.error('Failed to email patient');
    }
  };

  const columns = [
    { title: 'Member', dataIndex: 'name', key: 'name', render: (n) => n || '—' },
    {
      title: 'Kit code',
      dataIndex: 'activationCode',
      key: 'activationCode',
      render: (c) => c || '—',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => {
        const key = r.phase === 'rejected' || r.phase === 'cancelled' ? r.phase : r.status;
        const meta = STATUS_META[key] || { label: r.status, color: 'default' };
        if (r.status === 'error') {
          return (
            <Tooltip title={r.message}>
              <span><ExclamationCircleTwoTone twoToneColor="#ff4d4f" /> {meta.label}</span>
            </Tooltip>
          );
        }
        const tag = <Tag color={meta.color}>{meta.label}</Tag>;
        return r.labNote ? <Tooltip title={`Lab note: ${r.labNote}`}>{tag}</Tooltip> : tag;
      },
    },
    {
      title: 'Reported',
      dataIndex: 'resultsReadyAt',
      key: 'resultsReadyAt',
      render: (d) => fmtDate(d) || '—',
    },
    {
      title: 'Patient link',
      key: 'link',
      render: (_, r) =>
        r.status === 'ready' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Tooltip title="Copy a shareable results link for this patient">
              <Button size="small" icon={<LinkOutlined />} onClick={() => copyLink(r.patientId)}>
                Copy link
              </Button>
            </Tooltip>
            <Tooltip title="Email the results link to the patient">
              <Button size="small" icon={<MailOutlined />} onClick={() => emailPatient(r.patientId)}>
                Email
              </Button>
            </Tooltip>
          </div>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Modal
      title="Kit results from Endless Health"
      open={visible}
      onCancel={onClose}
      width={760}
      footer={[
        <Button key="refresh" onClick={sync} loading={loading}>
          Refresh
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      {!loading && rows.length > 0 && (
        <Alert
          style={{ marginBottom: 12 }}
          type={readyCount ? 'success' : 'info'}
          showIcon
          message={
            readyCount
              ? `${readyCount} of ${rows.length} have results ready.`
              : 'No results are ready yet. The lab posts results once processing completes.'
          }
        />
      )}
      <Table
        size="small"
        rowKey="patientId"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={rows.length > 8 ? { pageSize: 8 } : false}
        expandable={{
          rowExpandable: (r) => Array.isArray(r.results) && r.results.length > 0,
          expandedRowRender: (r) => (
            <Table
              size="small"
              rowKey={(x) => x.biomarker}
              pagination={false}
              dataSource={r.results || []}
              columns={[
                { title: 'Biomarker', dataIndex: 'biomarker', key: 'biomarker' },
                { title: 'Value', dataIndex: 'value', key: 'value' },
                { title: 'Units', dataIndex: 'units', key: 'units' },
              ]}
            />
          ),
        }}
      />
    </Modal>
  );
};

export default BulkKitResultsModal;
